import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Spinner,
  Badge,
  Form,
  Pagination,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaRupeeSign,
  FaClock,
  FaBookOpen,
  FaArrowRight,
  FaUsers,
  FaSearch,
} from "react-icons/fa";
import PublicNavbar from "../../components/public/Navbar";
import Footer from "../../components/public/Footer";
import CourseService from "../../services/courseService";
import "../../styles/courses.css";

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();
  // Pagination settings
  const getCoursesPerPage = () => {
    return window.innerWidth <= 768 ? 6 : 9;
  };

  const [coursesPerPage, setCoursesPerPage] = useState(getCoursesPerPage());

  useEffect(() => {
    loadCourses();
    window.scrollTo(0, 0);

    // Update courses per page on window resize
    const handleResize = () => {
      setCoursesPerPage(getCoursesPerPage());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Filter courses based on search term
    if (searchTerm.trim() === "") {
      setFilteredCourses(courses);
    } else {
      const filtered = courses.filter((course) =>
        course.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredCourses(filtered);
    }
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, courses]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await CourseService.getAllCourses();
      let coursesData = [];
      if (response && response.data && Array.isArray(response.data)) {
        coursesData = response.data;
      } else if (response && Array.isArray(response)) {
        coursesData = response;
      }
      setCourses(coursesData);
      setFilteredCourses(coursesData);
    } catch (error) {
      console.error("Error loading courses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleEnroll = (courseId) => {
    navigate(`/register?course=${courseId}`);
  };

  const formatStudentCount = (count) => {
    if (count > 1000) {
      return `${Math.floor(count / 1000)}k+`;
    }
    return count;
  };

  // Pagination logic
  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses.slice(
    indexOfFirstCourse,
    indexOfLastCourse,
  );
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
      />,
    );

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      }
      items.push(
        <Pagination.Item
          key={totalPages}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>,
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
      />,
    );

    return items;
  };

  return (
    <div className="courses-page-wrapper">
      <PublicNavbar />
      {/* Hero Section */}
      <div className="courses-page-hero">
        <Container>
          <Row className="justify-content-center text-center">
            <Col lg={8}>
              <h1 className="courses-page-hero-title animate-on-scroll">
                Our <span className="courses-page-text-gradient">Courses</span>
              </h1>
              <p className="courses-page-hero-subtitle animate-on-scroll">
                Choose from our wide range of industry-focused courses designed
                to make you job-ready
              </p>
            </Col>
          </Row>
        </Container>
        <div className="courses-page-hero-wave-bottom">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1440 200"
            preserveAspectRatio="none"
          >
            <path
              fill="#ffffff"
              fillOpacity="1"
              d="M0,96L48,112C96,128,192,160,288,160C384,160,480,128,576,122.7C672,117,768,139,864,154.7C960,171,1056,181,1152,170.7C1248,160,1344,128,1392,112L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </div>

      {/* Search Bar Section */}
      <Container className="courses-page-container py-4">
        <Row className="justify-content-center">
          <Col lg={8} md={10}>
            <Form className="courses-page-search-form">
              <div className="courses-page-search-wrapper">
                <FaSearch className="courses-page-search-icon" />
                <Form.Control
                  type="text"
                  placeholder="Search courses by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="courses-page-search-input"
                />
                {searchTerm && (
                  <Button
                    variant="link"
                    className="courses-page-search-clear"
                    onClick={() => setSearchTerm("")}
                  >
                    ×
                  </Button>
                )}
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
      {/* Courses Grid */}
      <Container className="courses-page-container py-3">
        {loading ? (
          <div className="courses-page-loading text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Loading courses...</p>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="courses-page-no-results text-center py-5">
            <h3 className="text-muted">No courses found</h3>
            <p className="text-muted">Try searching with different keywords.</p>
            {searchTerm && (
              <Button variant="primary" onClick={() => setSearchTerm("")}>
                Clear Search
              </Button>
            )}
          </div>
        ) : (
          <>
            <div className="courses-page-results-info mb-4">
              <p className="text-muted">
                Showing {indexOfFirstCourse + 1} -{" "}
                {Math.min(indexOfLastCourse, filteredCourses.length)} of{" "}
                {filteredCourses.length} courses
              </p>
            </div>

            <Row className="courses-page-grid g-4">
              {currentCourses.map((course, index) => {
                const totalStudents =
                  (course.base_student_count || 0) +
                  (course.enrolled_students || 0);
                return (
                  <Col lg={4} md={6} key={course.id}>
                    <div
                      className="courses-page-card animate-on-scroll"
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="courses-page-card-inner">
                        {/* Course Image */}
                        <div className="courses-page-card-image-wrapper">
                          {course.image_url ? (
                            <img
                              src={course.image_url}
                              alt={course.name}
                              className="courses-page-card-image"
                            />
                          ) : (
                            <div className="courses-page-card-image-placeholder">
                              <FaBookOpen size={50} className="text-primary" />
                            </div>
                          )}
                          {course.priority === 1 && (
                            <Badge className="courses-page-featured-badge">
                              Featured
                            </Badge>
                          )}
                        </div>

                        {/* Course Content */}
                        <div className="courses-page-card-content">
                          <h3 className="courses-page-card-title">
                            {course.name}
                          </h3>
                          <p className="courses-page-card-description">
                            {course.description?.substring(0, 100)}...
                          </p>

                          {/* Course Meta Info */}
                          <div className="courses-page-card-meta">
                            <div className="courses-page-meta-item">
                              <FaClock className="courses-page-meta-icon" />
                              <span>{course.duration}</span>
                            </div>
                            <div className="courses-page-meta-item">
                              <FaRupeeSign className="courses-page-meta-icon" />
                              <span>
                                {parseFloat(course.price).toLocaleString()}
                              </span>
                            </div>
                            <div className="courses-page-meta-item">
                              <FaUsers className="courses-page-meta-icon" />
                              <span>
                                {formatStudentCount(totalStudents)} Students
                              </span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="courses-page-card-actions">
                            <Button
                              variant="outline-primary"
                              className="courses-page-details-btn"
                              onClick={() => handleViewDetails(course.id)}
                            >
                              View Details <FaArrowRight className="ms-2" />
                            </Button>
                            <Button
                              variant="primary"
                              className="courses-page-enroll-btn"
                              onClick={() => handleEnroll(course.id)}
                            >
                              Register Now
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="courses-page-pagination-wrapper mt-5">
                <Pagination className="justify-content-center courses-page-pagination">
                  {renderPaginationItems()}
                </Pagination>
              </div>
            )}
          </>
        )}
      </Container>
      {/* CTA Section */}
      <div className="courses-page-cta">
        <Container className="text-center">
          <h2 className="display-5 fw-bold text-white mb-4">
            Not sure which course is right for you?
          </h2>
          <p className="lead text-white mb-4">
            Contact our career counselors for personalized guidance
          </p>
          <Button
            variant="light"
            size="lg"
            className="courses-page-cta-button"
            onClick={() => navigate("/contact")}
          >
            Talk to an Expert
          </Button>
        </Container>
      </div>
      <Footer />
    </div>
  );
};

export default Courses;
